import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, CheckCircle, AlertTriangle, Activity, TrendingUp, Clock, Info, ShieldAlert, BarChart2, ChevronRight, ChevronLeft, HelpCircle, Sun, Moon, Settings } from 'lucide-react';
import { createChart } from 'lightweight-charts';
import server from '../../../utils/serverConfig';
import apiClient from '../../../utils/apiClient';
import { calculateAdxDiBlueBars } from '../../../utils/adxDiBlueBars';
import '../../../utils/registerOpenAlgoIndicators';


const TradeSignalValidationModal = ({ tradeRecord, isOpen, onClose }) => {
    const chartContainerRef = useRef(null);
    const adxChartContainerRef = useRef(null);
    const diChartContainerRef = useRef(null);

    const mainChartRef = useRef(null);
    const adxChartRef = useRef(null);
    const diChartRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rawBars, setRawBars] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [signalValidation, setSignalValidation] = useState(null);
    const [hoveredCandle, setHoveredCandle] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showLegendHelp, setShowLegendHelp] = useState(false);
    const [showIndicatorSettings, setShowIndicatorSettings] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(true);

    const [indicatorParams, setIndicatorParams] = useState({
        diPeriod: 14,
        adxPeriod: 14,
        adxMaPeriod: 2,
        channelPeriod: 20,
    });


    const toggleSidebar = () => {
        setIsSidebarOpen(prev => !prev);
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 50);
    };

    const toggleTheme = () => {
        setIsDarkMode(prev => !prev);
    };

    // Dynamically update Lightweight Charts theme options when isDarkMode changes
    useEffect(() => {
        const themeOptions = {
            layout: {
                background: { type: 'solid', color: isDarkMode ? '#12141d' : '#FFFFFF' },
                textColor: isDarkMode ? '#9CA3AF' : '#334155',
            },
            grid: {
                vertLines: { color: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#E2E8F0' },
                horzLines: { color: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#E2E8F0' },
            },
            rightPriceScale: {
                borderColor: isDarkMode ? '#374151' : '#CBD5E1',
            },
            timeScale: {
                borderColor: isDarkMode ? '#374151' : '#CBD5E1',
            }
        };

        try {
            if (mainChartRef.current && chartContainerRef.current) {
                mainChartRef.current.applyOptions(themeOptions);
            }
            if (adxChartRef.current && adxChartContainerRef.current) {
                adxChartRef.current.applyOptions(themeOptions);
            }
            if (diChartRef.current && diChartContainerRef.current) {
                diChartRef.current.applyOptions(themeOptions);
            }
        } catch (e) {
            console.warn('Skipping theme apply on unmounted chart canvas:', e);
        }
    }, [isDarkMode]);





    // Selected Symbol & Trade Details
    const symbol = tradeRecord?.symbol_l || tradeRecord?.symbol_b || 'UNKNOWN';
    const strategy = tradeRecord?.strategy_tag_l || tradeRecord?.strategy_tag_b || 'Default Strategy';
    const buyTimeStr = tradeRecord?.buy_time_l || tradeRecord?.buy_time_b;
    const buyPrice = tradeRecord?.buy_price_l ?? tradeRecord?.buy_price_b;
    const sellTimeStr = tradeRecord?.sell_time_l || tradeRecord?.sell_time_b;
    const sellPrice = tradeRecord?.sell_price_l ?? tradeRecord?.sell_price_b;
    const status = tradeRecord?.status || 'MISMATCH';

    // Format date string for display (DD/MM, HH:MM:SS)
    const formatTime = (val) => {
        if (!val) return '-';
        try {
            const cleanStr = String(val).replace('T', ' ');
            const d = new Date(cleanStr);
            if (!isNaN(d.getTime())) {
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const hrs = String(d.getHours()).padStart(2, '0');
                const mins = String(d.getMinutes()).padStart(2, '0');
                const secs = String(d.getSeconds()).padStart(2, '0');
                return `${day}/${month}, ${hrs}:${mins}:${secs}`;
            }
            return String(val);
        } catch (e) {
            return String(val);
        }
    };


    // Robust Date / Timestamp Parser preserving exact HH:MM:SS
    const parseTimestampToSec = (str) => {
        if (!str) return null;
        if (typeof str === 'number') return Math.floor(str > 1e11 ? str / 1000 : str);
        
        try {
            const cleanStr = String(str).trim();
            // Handle ISO strings like 2026-08-27T09:41:02 or 2026-08-27 09:41:02
            if (cleanStr.includes('-')) {
                const norm = cleanStr.replace(' ', 'T');
                const d = new Date(norm);
                if (!isNaN(d.getTime())) {
                    return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()) / 1000);
                }
            }

            const parts = cleanStr.split(/[\s,]+/);
            if (parts.length >= 2) {
                const dateParts = parts[0].split(/[\/\-]/);
                const timeParts = parts[1].split(':');
                
                let day, month, year = new Date().getFullYear();
                if (dateParts.length === 3) {
                    if (parseInt(dateParts[0]) > 12) {
                        day = parseInt(dateParts[0]);
                        month = parseInt(dateParts[1]) - 1;
                        year = parseInt(dateParts[2]);
                    } else {
                        month = parseInt(dateParts[0]) - 1;
                        day = parseInt(dateParts[1]);
                        year = parseInt(dateParts[2]);
                    }
                } else if (dateParts.length === 2) {
                    day = parseInt(dateParts[0]);
                    month = parseInt(dateParts[1]) - 1;
                }

                const hours = parseInt(timeParts[0] || 0);
                const mins = parseInt(timeParts[1] || 0);
                const secs = parseInt(timeParts[2] || 0);

                return Math.floor(Date.UTC(year, month, day, hours, mins, secs) / 1000);
            }
        } catch (e) {
            console.warn("Could not parse date string:", str);
        }
        return null;
    };

    // Calculate PnL & Duration
    const calcReturn = () => {
        if (buyPrice && sellPrice && buyPrice > 0) {
            const diff = sellPrice - buyPrice;
            const pct = (diff / buyPrice) * 100;
            return { diff: diff.toFixed(2), pct: pct.toFixed(2), isProfit: diff >= 0 };
        }
        return null;
    };

    const returnMetrics = calcReturn();







    // Fetch Trade OHLCV & Indicators
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = `${server.godseye.baseUrl}trade-chart-data?symbol=${encodeURIComponent(symbol)}` +
                (buyTimeStr ? `&buy_time=${encodeURIComponent(buyTimeStr)}` : '') +
                (sellTimeStr ? `&sell_time=${encodeURIComponent(sellTimeStr)}` : '');

            const res = await apiClient.get(url);
            let candles = res.data?.candles || [];

            if (candles.length === 0) {
                setError(`No OHLCV candlestick data found in table 'ohlc_traded_symbols_60' for ${symbol}`);
                setChartData([]);
                setSignalValidation(null);
                return;
            }

            // Filter candles to Indian stock market session hours (09:15 to 15:30) for non-commodities
            const symUpper = symbol.toUpperCase();
            if (!symUpper.includes('COPPER') && !symUpper.includes('CRUDE') && !symUpper.includes('GOLD') && !symUpper.includes('SILVER') && !symUpper.includes('NATURALGAS')) {
                candles = candles.filter(c => {
                    if (!c.time) return true;
                    const d = new Date(c.time * 1000);
                    const hr = d.getUTCHours();
                    const min = d.getUTCMinutes();
                    const minsFromMidnight = hr * 60 + min;
                    return minsFromMidnight >= (9 * 60 + 15) && minsFromMidnight <= (15 * 60 + 30);
                });
            }

            // Store raw bars
            setRawBars(candles);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching historical candle data for chart modal:", err);
            setError("Failed to fetch OHLCV candlestick data from database API.");
            setChartData([]);
            setSignalValidation(null);
            setLoading(false);
        }
    }, [symbol, isOpen, buyTimeStr, strategy]);

    const handleParamChange = (key, rawVal) => {
        if (rawVal === '') {
            setIndicatorParams(p => ({ ...p, [key]: '' }));
            return;
        }
        const val = parseInt(rawVal, 10);
        if (!isNaN(val) && val >= 0) {
            setIndicatorParams(p => ({ ...p, [key]: val }));
        }
    };

    // Recalculate indicators whenever rawBars or indicatorParams change
    useEffect(() => {
        if (rawBars.length === 0) return;

        const safeParams = {
            diPeriod: typeof indicatorParams.diPeriod === 'number' && indicatorParams.diPeriod > 0 ? indicatorParams.diPeriod : 14,
            adxPeriod: typeof indicatorParams.adxPeriod === 'number' && indicatorParams.adxPeriod > 0 ? indicatorParams.adxPeriod : 14,
            adxMaPeriod: typeof indicatorParams.adxMaPeriod === 'number' && indicatorParams.adxMaPeriod >= 0 ? indicatorParams.adxMaPeriod : 2,
            channelPeriod: typeof indicatorParams.channelPeriod === 'number' && indicatorParams.channelPeriod > 0 ? indicatorParams.channelPeriod : 20,
        };

        const enriched = calculateAdxDiBlueBars(rawBars, safeParams);
        setChartData(enriched);


        // Determine signal validity at Buy Time
        let buyBarSignal = null;
        const buyTs = parseTimestampToSec(buyTimeStr);

        if (enriched.length > 0) {
            let closest = null;
            if (buyTs) {
                let matchIdx = -1;
                for (let i = 0; i < enriched.length; i++) {
                    if (enriched[i].time <= buyTs) {
                        matchIdx = i;
                    } else {
                        break;
                    }
                }
                if (matchIdx !== -1 && Math.abs(buyTs - enriched[matchIdx].time) <= 86400) {
                    closest = enriched[matchIdx];
                }
            }
            if (!closest) {
                closest = enriched[Math.floor(enriched.length / 2)];
            }


            if (closest) {
                // Strategy-aware signal validity check
                const stratUpper = String(strategy).toUpperCase();
                let isValidSignal = false;
                let signalReason = "";

                if (stratUpper.includes("PDI_MA")) {
                    isValidSignal = (closest.ADX !== null && closest.ADXMA !== null && closest.ADX > closest.ADXMA) || (closest.PDI !== null && closest.MDI !== null && closest.PDI > closest.MDI);
                    signalReason = "PDI / ADX momentum above MA at buy time";
                } else if (stratUpper.includes("PDI_MDI")) {
                    isValidSignal = (closest.PDI !== null && closest.MDI !== null && closest.PDI > closest.MDI);
                    signalReason = "+DI (PDI) leading -DI (MDI) at buy time";
                } else {
                    isValidSignal = closest.blue_bar === 1;
                    signalReason = "ADX > ADXMA and +DI > -DI (Blue Bar active)";
                }

                buyBarSignal = {
                    is_valid: isValidSignal,
                    blue_bar: closest.blue_bar === 1,
                    reason: signalReason,
                    adx: closest.ADX,
                    adxma: closest.ADXMA,
                    pdi: closest.PDI,
                    mdi: closest.MDI,
                    time: closest.time
                };
            }
        }
        setSignalValidation(buyBarSignal);
    }, [rawBars, indicatorParams, buyTimeStr, strategy]);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen, fetchData]);

    // Render Multi-Pane Charts using Lightweight Charts / OpenAlgo engine
    useEffect(() => {
        if (!isOpen || loading || chartData.length === 0) return;

        let mainChart = null;
        let adxChart = null;
        let diChart = null;

        const mainContainer = chartContainerRef.current;
        const adxContainer = adxChartContainerRef.current;
        const diContainer = diChartContainerRef.current;

        if (!mainContainer || !adxContainer || !diContainer) return;

        // Safely destroy existing chart instances before clearing innerHTML
        if (mainChartRef.current) {
            try { mainChartRef.current.remove(); } catch (e) {}
            mainChartRef.current = null;
        }
        if (adxChartRef.current) {
            try { adxChartRef.current.remove(); } catch (e) {}
            adxChartRef.current = null;
        }
        if (diChartRef.current) {
            try { diChartRef.current.remove(); } catch (e) {}
            diChartRef.current = null;
        }

        // Clear previous container content
        mainContainer.innerHTML = '';
        adxContainer.innerHTML = '';
        diContainer.innerHTML = '';


        const bgColor = isDarkMode ? '#12141d' : '#FFFFFF';
        const textColor = isDarkMode ? '#9CA3AF' : '#334155';
        const gridColor = isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#E2E8F0';
        const borderColor = isDarkMode ? '#374151' : '#CBD5E1';

        const commonOptions = {
            layout: {
                background: { type: 'solid', color: bgColor },
                textColor: textColor,
                fontSize: 11,
                fontFamily: "'Inter', 'Trebuchet MS', sans-serif",
            },
            grid: {
                vertLines: { color: isDarkMode ? 'rgba(255,255,255,0.04)' : '#E8ECF0', style: 0 },
                horzLines: { color: isDarkMode ? 'rgba(255,255,255,0.04)' : '#E8ECF0', style: 0 },
            },
            rightPriceScale: {
                borderColor: borderColor,
                borderVisible: true,
                entireTextOnly: false,
                ticksVisible: true,
                scaleMargins: { top: 0.1, bottom: 0.1 },
            },
            crosshair: {
                mode: 0,
                vertLine: {
                    width: 1,
                    color: isDarkMode ? 'rgba(200,200,200,0.4)' : 'rgba(0,0,0,0.3)',
                    style: 0,
                    visible: true,
                    labelVisible: true,
                    labelBackgroundColor: isDarkMode ? '#374151' : '#1e293b',
                },
                horzLine: {
                    width: 1,
                    color: isDarkMode ? 'rgba(200,200,200,0.4)' : 'rgba(0,0,0,0.3)',
                    style: 0,
                    visible: true,
                    labelVisible: true,
                    labelBackgroundColor: isDarkMode ? '#374151' : '#1e293b',
                },
            },
            timeScale: {
                borderColor: borderColor,
                borderVisible: true,
                timeVisible: true,
                secondsVisible: false,
                fixLeftEdge: false,
                fixRightEdge: false,
                tickMarkFormatter: (time) => {
                    if (typeof time !== 'number') return String(time);
                    const date = new Date(time * 1000);
                    const day = String(date.getUTCDate()).padStart(2, '0');
                    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                    const hours = String(date.getUTCHours()).padStart(2, '0');
                    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                    return `${day}/${month} ${hours}:${minutes}`;
                },
            },
            localization: {
                locale: 'en-IN',
                timeFormatter: (time) => {
                    if (typeof time !== 'number') return String(time);
                    const date = new Date(time * 1000);
                    const day = String(date.getUTCDate()).padStart(2, '0');
                    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                    const hours = String(date.getUTCHours()).padStart(2, '0');
                    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                    return `${day}/${month}, ${hours}:${minutes}`;
                },
            },
            handleScroll: { mouseWheel: true, pressedMouseMove: true },
            handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
        };


        // 1. Main Candlestick Chart
        mainChart = createChart(mainContainer, {
            ...commonOptions,
            width: mainContainer.clientWidth,
            height: mainContainer.clientHeight || 280,
            rightPriceScale: {
                ...commonOptions.rightPriceScale,
                borderColor: borderColor,
                minimumWidth: 70,
            },
        });
        mainChartRef.current = mainChart;



        const candlestickSeries = mainChart.addCandlestickSeries({
            upColor: '#10B981',
            downColor: '#EF4444',
            borderUpColor: '#10B981',
            borderDownColor: '#EF4444',
            wickUpColor: '#10B981',
            wickDownColor: '#EF4444',
            priceLineVisible: false, // Hides horizontal last-price line extending across canvas
            lastValueVisible: true,  // Keeps price tag on right Y-axis scale
        });


        // Map candles and apply blue color override for Blue Bars
        const formattedCandles = chartData.map(c => {
            const isBlue = c.blue_bar === 1;
            return {
                time: c.time,
                open: c.open,
                high: c.high,
                low: c.low,
                close: c.close,
                color: isBlue ? '#3B82F6' : (c.close >= c.open ? '#10B981' : '#EF4444'),
                wickColor: isBlue ? '#60A5FA' : (c.close >= c.open ? '#10B981' : '#EF4444'),
                borderColor: isBlue ? '#3B82F6' : (c.close >= c.open ? '#10B981' : '#EF4444'),
            };
        });

        candlestickSeries.setData(formattedCandles);

        // Helper to find exact candlestick index during which trade executed (candle start time <= targetSec)
        const findClosestCandleIndex = (targetSec, maxDiffSeconds = 86400) => {
            if (!targetSec || chartData.length === 0) return -1;
            let matchIdx = -1;
            for (let i = 0; i < chartData.length; i++) {
                if (chartData[i].time <= targetSec) {
                    matchIdx = i;
                } else {
                    break;
                }
            }
            if (matchIdx === -1) {
                if (Math.abs(chartData[0].time - targetSec) <= maxDiffSeconds) return 0;
                return -1;
            }
            if (Math.abs(targetSec - chartData[matchIdx].time) > maxDiffSeconds) {
                return -1;
            }
            return matchIdx;
        };


        const buyTsRaw = parseTimestampToSec(buyTimeStr);
        const sellTsRaw = parseTimestampToSec(sellTimeStr);

        const buyIdx = findClosestCandleIndex(buyTsRaw);
        const sellIdx = findClosestCandleIndex(sellTsRaw);

        const buyTs = buyIdx !== -1 && chartData[buyIdx] ? chartData[buyIdx].time : null;
        const sellTs = sellIdx !== -1 && chartData[sellIdx] ? chartData[sellIdx].time : null;


        // Donchian Overlays (Full dataset overlay across all candles)
        const upperSeries = mainChart.addLineSeries({ color: '#818CF8', lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false }); // dashed indigo
        const basisSeries = mainChart.addLineSeries({ color: '#FBBF24', lineWidth: 1, lastValueVisible: false, priceLineVisible: false }); // solid yellow
        const lowerSeries = mainChart.addLineSeries({ color: '#818CF8', lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false }); // dashed indigo

        upperSeries.setData(chartData.filter(c => c.upper !== null).map(c => ({ time: c.time, value: c.upper })));
        basisSeries.setData(chartData.filter(c => c.basis !== null).map(c => ({ time: c.time, value: c.basis })));
        lowerSeries.setData(chartData.filter(c => c.lower !== null).map(c => ({ time: c.time, value: c.lower })));



        // Trade Execution Markers
        const markers = [];

        if (buyTs && buyPrice) {
            markers.push({
                time: buyTs,
                position: 'belowBar',
                color: '#10B981',
                shape: 'arrowUp',
                text: `BUY @ ${Number(buyPrice).toFixed(2)}`,
            });
        }
        if (sellTs && sellPrice) {
            markers.push({
                time: sellTs,
                position: 'aboveBar',
                color: '#EF4444',
                shape: 'arrowDown',
                text: `SELL @ ${Number(sellPrice).toFixed(2)}`,
            });
        }
        if (markers.length > 0) {
            // Sort markers by time
            markers.sort((a, b) => a.time - b.time);
            candlestickSeries.setMarkers(markers);
        }

        // Subtle Vertical Green Highlight Band for Trade Holding Window (Buy Entry to Sell Exit)
        if (buyTs && sellTs && buyTs <= sellTs) {
            const holdingBgSeries = mainChart.addHistogramSeries({
                priceScaleId: 'trade_holding_bg_scale',
                priceFormat: { type: 'volume' },
                lastValueVisible: false, // Hides the green '100' value box on right price scale
                priceLineVisible: false,
            });

            mainChart.priceScale('trade_holding_bg_scale').applyOptions({
                scaleMargins: { top: 0, bottom: 0 },
                visible: false,
            });

            const holdingData = chartData
                .filter(c => c.time >= buyTs && c.time <= sellTs)
                .map(c => ({
                    time: c.time,
                    value: 100,
                    color: isDarkMode ? 'rgba(16, 185, 129, 0.16)' : 'rgba(16, 185, 129, 0.22)', // Subtle translucent vertical green highlight band
                }));

            if (holdingData.length > 0) {
                holdingBgSeries.setData(holdingData);
            }
        }



        // 2. ADX Sub-Pane Chart
        adxChart = createChart(adxContainer, {
            ...commonOptions,
            width: adxContainer.clientWidth,
            height: adxContainer.clientHeight || 80,
            rightPriceScale: {
                ...commonOptions.rightPriceScale,
                borderColor: borderColor,
                minimumWidth: 70,
            },
        });
        adxChartRef.current = adxChart;

        const adxSeries = adxChart.addLineSeries({ color: '#A855F7', lineWidth: 2, title: 'ADX' });
        const adxMaSeries = adxChart.addLineSeries({ color: '#F97316', lineWidth: 1.5, title: 'ADXMA' });

        adxSeries.setData(chartData.filter(c => c.ADX !== null).map(c => ({ time: c.time, value: c.ADX })));
        adxMaSeries.setData(chartData.filter(c => c.ADXMA !== null).map(c => ({ time: c.time, value: c.ADXMA })));

        // 3. Directional Indicators Sub-Pane Chart (+DI vs -DI)
        diChart = createChart(diContainer, {
            ...commonOptions,
            width: diContainer.clientWidth,
            height: diContainer.clientHeight || 80,
            rightPriceScale: {
                ...commonOptions.rightPriceScale,
                borderColor: borderColor,
                minimumWidth: 70,
            },
        });
        diChartRef.current = diChart;


        const pdiSeries = diChart.addLineSeries({ color: '#10B981', lineWidth: 2, title: '+DI' });
        const mdiSeries = diChart.addLineSeries({ color: '#EF4444', lineWidth: 2, title: '-DI' });

        pdiSeries.setData(chartData.filter(c => c.PDI !== null).map(c => ({ time: c.time, value: c.PDI })));
        mdiSeries.setData(chartData.filter(c => c.MDI !== null).map(c => ({ time: c.time, value: c.MDI })));

        // Auto-zoom focused on Buy Entry & Sell Exit execution window
        if (chartData.length > 0) {
            const buySec = parseTimestampToSec(buyTimeStr);
            const sellSec = parseTimestampToSec(sellTimeStr);

            const buyIdx = findClosestCandleIndex(buySec);
            const sellIdx = findClosestCandleIndex(sellSec);



            if (buyIdx !== -1) {
                const endIdx = sellIdx !== -1 ? sellIdx : buyIdx;
                const fromIdx = Math.max(0, buyIdx - 15);
                const toIdx = Math.min(chartData.length - 1, endIdx + 15);
                
                if (chartData[fromIdx] && chartData[toIdx]) {
                    const targetRange = {
                        from: chartData[fromIdx].time,
                        to: chartData[toIdx].time,
                    };
                    mainChart.timeScale().setVisibleRange(targetRange);
                    if (adxChart && adxChart.timeScale) adxChart.timeScale().setVisibleRange(targetRange);
                    if (diChart && diChart.timeScale) diChart.timeScale().setVisibleRange(targetRange);
                } else {
                    mainChart.timeScale().fitContent();
                }
            } else {
                mainChart.timeScale().fitContent();
            }
        } else {
            mainChart.timeScale().fitContent();
        }


        let isSyncing = false;
        const handleTimeRangeChange = (range) => {
            if (isSyncing || !range || range.from === null || range.to === null || range.from === undefined || range.to === undefined) {
                return;
            }
            isSyncing = true;
            try {
                if (adxChart && adxChart.timeScale) {
                    adxChart.timeScale().setVisibleRange(range);
                }
                if (diChart && diChart.timeScale) {
                    diChart.timeScale().setVisibleRange(range);
                }
            } catch (err) {
                // Ignore transient range sync errors
            } finally {
                isSyncing = false;
            }
        };

        mainChart.timeScale().subscribeVisibleTimeRangeChange(handleTimeRangeChange);


        // Crosshair Hover Handler for top-left OHLCV display
        mainChart.subscribeCrosshairMove((param) => {
            if (
                param === undefined ||
                !param.time ||
                param.point === undefined ||
                param.point.x < 0 ||
                param.point.x > mainContainer.clientWidth ||
                param.point.y < 0 ||
                param.point.y > mainContainer.clientHeight
            ) {
                setHoveredCandle(null);
                return;
            }

            const candlePrices = param.seriesPrices.get(candlestickSeries);
            const rawCursorPrice = candlestickSeries.coordinateToPrice(param.point.y);

            if (candlePrices || (typeof rawCursorPrice === 'number' && !isNaN(rawCursorPrice))) {
                const match = chartData.find(c => c.time === param.time);
                setHoveredCandle({
                    open: candlePrices ? candlePrices.open : null,
                    high: candlePrices ? candlePrices.high : null,
                    low: candlePrices ? candlePrices.low : null,
                    close: candlePrices ? candlePrices.close : null,
                    volume: match ? match.volume : null,
                    blue_bar: match ? match.blue_bar : 0,
                    time: param.time,
                    cursorPrice: typeof rawCursorPrice === 'number' && !isNaN(rawCursorPrice) && rawCursorPrice > 0 ? rawCursorPrice : null
                });
            } else {
                setHoveredCandle(null);
            }
        });


        // Resize Listener
        const handleResize = () => {
            try {
                if (mainContainer && mainContainer.clientWidth && mainChartRef.current) {
                    mainChartRef.current.applyOptions({ width: mainContainer.clientWidth });
                }
                if (adxContainer && adxContainer.clientWidth && adxChartRef.current) {
                    adxChartRef.current.applyOptions({ width: adxContainer.clientWidth });
                }
                if (diContainer && diContainer.clientWidth && diChartRef.current) {
                    diChartRef.current.applyOptions({ width: diContainer.clientWidth });
                }
            } catch (e) {
                // Ignore resize calls on unmounted canvas elements
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (mainChartRef.current) {
                try { mainChartRef.current.remove(); } catch (e) {}
                mainChartRef.current = null;
            }
            if (adxChartRef.current) {
                try { adxChartRef.current.remove(); } catch (e) {}
                adxChartRef.current = null;
            }
            if (diChartRef.current) {
                try { diChartRef.current.remove(); } catch (e) {}
                diChartRef.current = null;
            }
        };

    }, [isOpen, loading, chartData, buyTimeStr, buyPrice, sellTimeStr, sellPrice]);

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-[99999] p-3 lg:p-4 flex flex-col w-screen h-screen overflow-hidden transition-colors ${
            isDarkMode ? 'bg-[#0b0c10] text-gray-200' : 'bg-slate-950 text-slate-800'
        }`}>

            {/* Streamlined Ultra-Compact Top Bar */}
            <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 my-1 rounded-xl border shadow-xl shrink-0 transition-colors ${
                isDarkMode ? 'bg-[#14161f] border-gray-800' : 'bg-white border-slate-200 text-slate-800'
            }`}>
                {/* Left: Symbol & Strategy Info */}
                <div className="flex items-center gap-2.5">
                    <div className={`p-1.5 rounded-lg border ${isDarkMode ? 'bg-blue-600/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                        <Activity className="w-4 h-4" />
                    </div>
                    <div className="flex items-center gap-2">
                        <h2 className={`text-sm font-bold tracking-wide ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{symbol}</h2>
                        <span className={`text-[11px] px-2 py-0.5 rounded border font-medium ${isDarkMode ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' : 'text-blue-700 bg-blue-50 border-blue-200'}`}>{strategy}</span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${
                            status === 'Match' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
                        }`}>{status}</span>
                    </div>
                </div>

                {/* Middle: Compact Trade Execution Metrics Strip */}
                <div className="hidden md:flex items-center gap-3 text-xs font-mono">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${isDarkMode ? 'bg-[#0f1116] border-gray-800' : 'bg-slate-100 border-slate-200'}`}>
                        <span className="text-emerald-400 font-bold text-[10px]">BUY:</span>
                        <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{buyPrice ? `₹${Number(buyPrice).toFixed(2)}` : '-'}</span>
                        <span className="text-gray-400 text-[10px] ml-1">({formatTime(buyTimeStr)})</span>
                    </div>

                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${isDarkMode ? 'bg-[#0f1116] border-gray-800' : 'bg-slate-100 border-slate-200'}`}>
                        <span className="text-red-400 font-bold text-[10px]">SELL:</span>
                        <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{sellPrice ? `₹${Number(sellPrice).toFixed(2)}` : '-'}</span>
                        <span className="text-gray-400 text-[10px] ml-1">({formatTime(sellTimeStr)})</span>
                    </div>

                    {returnMetrics && (
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${isDarkMode ? 'bg-[#0f1116] border-gray-800' : 'bg-slate-100 border-slate-200'}`}>
                            <span className="text-gray-400 text-[10px]">P&amp;L:</span>
                            <span className={`font-bold ${returnMetrics.isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                                {returnMetrics.isProfit ? '+' : ''}{returnMetrics.pct}% ({returnMetrics.diff})
                            </span>
                        </div>
                    )}
                </div>

                {/* Right: Theme Toggle & Close Button */}
                <div className="flex items-center gap-2.5">
                    {/* Light / Dark Mode Toggle Button */}
                    <button
                        onClick={toggleTheme}
                        title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        className={`p-1.5 px-2.5 rounded-lg border transition-all flex items-center gap-1.5 text-xs font-semibold ${
                            isDarkMode 
                                ? 'bg-gray-800/80 hover:bg-gray-700 text-amber-300 border-gray-700' 
                                : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300'
                        }`}
                    >
                        {isDarkMode ? <Sun className="w-3.5 h-3.5 text-amber-400" /> : <Moon className="w-3.5 h-3.5 text-indigo-600" />}
                        <span className="text-[11px] font-sans">{isDarkMode ? 'Light' : 'Dark'}</span>
                    </button>

                    <button
                        onClick={onClose}
                        className={`p-1.5 rounded-lg transition-all border ${
                            isDarkMode 
                                ? 'text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 border-gray-700' 
                                : 'text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border-slate-300'
                        }`}
                    >
                        <X className="w-4.5 h-4.5" />
                    </button>
                </div>
            </div>


            {/* Main Content Area: Chart + Sidebar */}
            <div className="flex-1 grid grid-cols-1 xl:grid-cols-4 gap-4 min-h-0 overflow-hidden">
                {/* Left/Middle: Multi-Pane Charts */}
                <div className={`${isSidebarOpen ? 'xl:col-span-3' : 'xl:col-span-4'} ${isDarkMode ? 'bg-[#14161f] border-gray-800' : 'bg-white border-slate-200 text-slate-800'} border rounded-xl p-3 flex flex-col min-h-0 overflow-hidden relative transition-all duration-300`}>

                    {loading && (
                        <div className="absolute inset-0 bg-[#12141d]/80 z-20 flex items-center justify-center text-sm text-blue-400 font-semibold gap-2">
                            <Activity className="w-5 h-5 animate-spin" /> Loading OHLCV Candles & Signal Lines...
                        </div>
                    )}

                    {/* Error / Missing Data Banner Overlay */}
                    {error || (!loading && chartData.length === 0) ? (
                        <div className="absolute inset-0 bg-[#12141d]/95 z-40 flex flex-col items-center justify-center text-center p-6 gap-3 rounded-xl border border-gray-800 backdrop-blur-md">
                            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-400">
                                <AlertTriangle className="w-8 h-8 animate-pulse" />
                            </div>
                            <h4 className="text-base font-bold text-white tracking-wide">No OHLCV Data Found in Database</h4>
                            <p className="text-xs text-gray-400 max-w-md leading-relaxed">
                                {error || `Historical candlestick records for ${symbol} are not available in database table 'ohlc_traded_symbols_60'.`}
                            </p>
                            <span className="text-[11px] text-gray-400 bg-gray-900/90 px-3 py-1.5 rounded-lg border border-gray-800 font-mono mt-1">
                                Symbol: <strong className="text-blue-400">{symbol}</strong>
                            </span>
                        </div>
                    ) : null}

                    {/* Missing / Outdated Data Warning Banner */}
                    {(buyTimeStr || sellTimeStr) && chartData.length > 0 && (() => {
                        const buyTsRaw = parseTimestampToSec(buyTimeStr);
                        const sellTsRaw = parseTimestampToSec(sellTimeStr);
                        const lastCandleTs = chartData[chartData.length - 1]?.time;

                        if (!lastCandleTs) return null;

                        const isBuyMissing = buyTsRaw && (buyTsRaw - lastCandleTs > 86400);
                        const isSellMissing = sellTsRaw && (sellTsRaw - lastCandleTs > 86400);

                        if (!isBuyMissing && !isSellMissing) return null;

                        // Helper to format date cleanly
                        const formatDateClean = (rawStr) => {
                            if (!rawStr) return '';
                            const d = new Date(rawStr.replace('T', ' '));
                            if (!isNaN(d.getTime())) {
                                const dd = String(d.getDate()).padStart(2, '0');
                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                const yyyy = d.getFullYear();
                                return `${dd}/${mm}/${yyyy}`;
                            }
                            return rawStr.split('T')[0].split(' ')[0];
                        };

                        // Format last candle timestamp cleanly (DD/MM/YYYY HH:MM)
                        const dCandle = new Date(lastCandleTs * 1000);
                        const cDD = String(dCandle.getUTCDate()).padStart(2, '0');
                        const cMM = String(dCandle.getUTCMonth() + 1).padStart(2, '0');
                        const cYYYY = dCandle.getUTCFullYear();
                        const cHH = String(dCandle.getUTCHours()).padStart(2, '0');
                        const cMin = String(dCandle.getUTCMinutes()).padStart(2, '0');
                        const lastCandleDateStr = `${cDD}/${cMM}/${cYYYY} ${cHH}:${cMin}`;

                        let missingLabel = '';
                        if (isBuyMissing && isSellMissing) {
                            missingLabel = `Trade Buy (${formatDateClean(buyTimeStr)}) & Sell (${formatDateClean(sellTimeStr)}) Dates`;
                        } else if (isBuyMissing) {
                            missingLabel = `Buy Entry Date (${formatDateClean(buyTimeStr)})`;
                        } else {
                            missingLabel = `Sell Exit Date (${formatDateClean(sellTimeStr)})`;
                        }

                        return (
                            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-3 py-2 rounded-lg flex items-center justify-between gap-3 text-xs mb-2 shadow-sm font-sans">
                                <div className="flex items-center gap-2 font-medium">
                                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 animate-pulse" />
                                    <span>
                                        <strong className="text-amber-200 uppercase tracking-wide font-bold">OHLCV Candle Data Missing for {missingLabel}:</strong> Database candles currently end on <strong className="text-white font-mono bg-gray-900 px-1.5 py-0.5 rounded border border-gray-800">{lastCandleDateStr}</strong>. {isSellMissing && !isBuyMissing ? 'Sell exit marker & trade holding window are hidden' : 'Markers are hidden'} until missing candles are loaded into table <code className="text-amber-300 font-mono text-[11px]">ohlc_traded_symbols_60</code>.
                                    </span>
                                </div>
                            </div>
                        );
                    })()}



                    {/* Top Left Chart Legend / Hovered Candle OHLCV Header */}
                    <div className={`flex flex-wrap items-center justify-between gap-3 pb-2 text-xs shrink-0 px-3 py-1.5 rounded-lg border transition-colors ${
                        isDarkMode ? 'bg-[#0f1116] border-gray-800 text-gray-300' : 'bg-slate-100 border-slate-200 text-slate-800'
                    }`}>

                        <div className="flex items-center gap-3 font-mono">
                            {hoveredCandle ? (
                                <div className="flex flex-wrap items-center gap-2.5 text-[11px]">
                                    {hoveredCandle.open !== null && <span className={isDarkMode ? 'text-gray-400' : 'text-slate-500'}>O: <strong className={isDarkMode ? 'text-white' : 'text-slate-900'}>{Number(hoveredCandle.open).toFixed(2)}</strong></span>}

                                    {hoveredCandle.high !== null && <span className={isDarkMode ? 'text-gray-400' : 'text-slate-500'}>H: <strong className="text-emerald-500 font-bold">{Number(hoveredCandle.high).toFixed(2)}</strong></span>}
                                    {hoveredCandle.low !== null && <span className={isDarkMode ? 'text-gray-400' : 'text-slate-500'}>L: <strong className="text-red-500 font-bold">{Number(hoveredCandle.low).toFixed(2)}</strong></span>}
                                    {hoveredCandle.close !== null && <span className={isDarkMode ? 'text-gray-400' : 'text-slate-500'}>C: <strong className={hoveredCandle.close >= hoveredCandle.open ? 'text-emerald-500 font-bold' : 'text-red-500 font-bold'}>{Number(hoveredCandle.close).toFixed(2)}</strong></span>}
                                    {hoveredCandle.volume !== null && hoveredCandle.volume !== undefined && (
                                        <span className={isDarkMode ? 'text-gray-400' : 'text-slate-500'}>V: <strong className="text-blue-500 font-bold">{Number(hoveredCandle.volume).toLocaleString()}</strong></span>
                                    )}
                                    {hoveredCandle.blue_bar === 1 && (
                                        <span className="text-[10px] bg-blue-500/20 text-blue-500 border border-blue-500/30 px-1.5 py-0.5 rounded font-bold">BLUE BAR</span>
                                    )}
                                </div>

                            ) : (
                                <div className="flex items-center gap-3 text-xs text-gray-400">
                                    <span className={`font-semibold flex items-center gap-1 ${isDarkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span> Blue Bar
                                    </span>
                                    <span className="text-indigo-500 font-medium flex items-center gap-1">
                                        <span className="w-2 h-0.5 bg-indigo-500 inline-block"></span> Donchian Channel
                                    </span>
                                    <span className="text-gray-400 italic text-[11px] ml-1 hidden sm:inline">Hover over candles for OHLCV</span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Expand Sidebar Button (when collapsed) */}
                            {!isSidebarOpen && (
                                <button
                                    onClick={toggleSidebar}
                                    title="Expand Audit Matrix Sidebar"
                                    className="p-1 px-2.5 text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 rounded-md border border-blue-500/30 flex items-center gap-1 text-[11px] font-sans font-semibold transition-all shadow-sm"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                    <span>Audit Matrix</span>
                                </button>
                            )}

                            {/* Interactive Indicator Settings Icon */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowIndicatorSettings(prev => !prev)}
                                    className="p-1 px-2.5 text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-md border border-indigo-500/30 flex items-center gap-1.5 text-[11px] font-sans font-medium transition-all"
                                    title="Edit Indicator Settings & Parameters"
                                >
                                    <Settings className="w-3.5 h-3.5" />
                                    <span>Settings</span>
                                </button>

                                {/* Settings Popover Modal */}
                                {showIndicatorSettings && (
                                    <div className={`absolute top-8 right-0 z-50 w-72 border rounded-xl p-4 shadow-2xl text-xs space-y-3 ${
                                        isDarkMode ? 'bg-[#141622] border-indigo-500/40 text-gray-200' : 'bg-white border-indigo-300 text-slate-800 shadow-slate-300'
                                    }`}>
                                        <div className={`flex items-center justify-between border-b pb-2 ${isDarkMode ? 'border-gray-800' : 'border-slate-200'}`}>
                                            <span className={`font-bold uppercase text-[11px] tracking-wider flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                                <Settings className="w-3.5 h-3.5 text-indigo-500" />
                                                Indicator Parameters
                                            </span>
                                            <button onClick={() => setShowIndicatorSettings(false)} className="text-gray-400 hover:text-gray-600">
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>

                                        <div className="space-y-2.5 font-sans">
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span>DI Length</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={indicatorParams.diPeriod}
                                                    onChange={(e) => handleParamChange('diPeriod', e.target.value)}
                                                    className={`w-16 px-2 py-1 border rounded text-right font-mono text-xs ${
                                                        isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-slate-100 border-slate-300 text-slate-900'
                                                    }`}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span>ADX Smoothing</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={indicatorParams.adxPeriod}
                                                    onChange={(e) => handleParamChange('adxPeriod', e.target.value)}
                                                    className={`w-16 px-2 py-1 border rounded text-right font-mono text-xs ${
                                                        isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-slate-100 border-slate-300 text-slate-900'
                                                    }`}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span>ADX MA Period</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="50"
                                                    value={indicatorParams.adxMaPeriod}
                                                    onChange={(e) => handleParamChange('adxMaPeriod', e.target.value)}
                                                    className={`w-16 px-2 py-1 border rounded text-right font-mono text-xs ${
                                                        isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-slate-100 border-slate-300 text-slate-900'
                                                    }`}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span>Donchian Length</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="200"
                                                    value={indicatorParams.channelPeriod}
                                                    onChange={(e) => handleParamChange('channelPeriod', e.target.value)}
                                                    className={`w-16 px-2 py-1 border rounded text-right font-mono text-xs ${
                                                        isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-slate-100 border-slate-300 text-slate-900'
                                                    }`}
                                                />
                                            </div>

                                        </div>

                                        <div className="pt-1 flex justify-end">
                                            <button
                                                onClick={() => setIndicatorParams({ diPeriod: 14, adxPeriod: 14, adxMaPeriod: 2, channelPeriod: 20 })}
                                                className="text-[10px] text-indigo-400 hover:text-indigo-300 underline font-medium"
                                            >
                                                Reset Defaults
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Interactive Chart Guide Info Icon */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowLegendHelp(prev => !prev)}
                                    onMouseEnter={() => setShowLegendHelp(true)}
                                    className="p-1 px-2 text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 rounded-md border border-blue-500/30 flex items-center gap-1.5 text-[11px] font-sans font-medium transition-all"
                                    title="View Chart Line & Marker Legend"
                                >
                                    <HelpCircle className="w-3.5 h-3.5" />
                                    <span>Chart Guide</span>
                                </button>



                                {/* Guide Popover Tooltip */}
                                {showLegendHelp && (
                                    <div
                                        onMouseLeave={() => setShowLegendHelp(false)}
                                        className={`absolute top-8 right-0 z-50 w-72 border rounded-xl p-3.5 shadow-2xl text-xs space-y-2.5 ${
                                            isDarkMode ? 'bg-[#141622] border-blue-500/40 text-gray-300' : 'bg-white border-blue-300 text-slate-800 shadow-slate-300'
                                        }`}
                                    >
                                        <div className={`flex items-center justify-between border-b pb-1.5 ${isDarkMode ? 'border-gray-800' : 'border-slate-200'}`}>
                                            <span className={`font-bold uppercase text-[11px] tracking-wider flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                                <Info className="w-3.5 h-3.5 text-blue-500" />
                                                Chart Line & Marker Guide
                                            </span>
                                            <button onClick={() => setShowLegendHelp(false)} className="text-gray-400 hover:text-gray-600">
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>

                                        <div className="space-y-2 text-[11px] font-sans">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 shrink-0"></span>
                                                <span><strong className="text-blue-500">Blue Candle</strong>: ADX &gt; ADXMA &amp; +DI &gt; -DI</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-4 h-0 border-b-2 border-dashed border-indigo-500 shrink-0"></span>
                                                <span><strong className="text-indigo-500">Top Dotted Line</strong>: Donchian Upper (20-bar High)</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-4 h-0.5 bg-amber-500 shrink-0"></span>
                                                <span><strong className="text-amber-500">Middle Yellow Line</strong>: Donchian Basis (20-bar Midpoint)</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-4 h-0 border-b-2 border-dashed border-indigo-500 shrink-0"></span>
                                                <span><strong className="text-indigo-500">Bottom Dotted Line</strong>: Donchian Lower (20-bar Low)</span>
                                            </div>
                                            <div className={`flex items-center gap-2 border-t pt-1.5 ${isDarkMode ? 'border-gray-800' : 'border-slate-200'}`}>
                                                <span className="w-3 h-3 bg-emerald-500/20 border border-emerald-500/50 rounded-sm shrink-0"></span>
                                                <span><strong className="text-emerald-500">Green Vertical Band</strong>: Trade Holding Period (Buy to Sell)</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-emerald-500 font-bold shrink-0">🟩 BUY</span>
                                                <span>Green Up Arrow: Buy Entry Marker</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-red-500 font-bold shrink-0">🟥 SELL</span>
                                                <span>Red Down Arrow: Sell Exit Marker</span>
                                            </div>


                                            <div className={`flex items-center gap-2 border-t pt-1.5 ${isDarkMode ? 'border-gray-800' : 'border-slate-200'}`}>
                                                <span className="text-purple-500 font-bold shrink-0">Sub-Panes</span>
                                                <span>Pane 1: ADX/ADXMA. Pane 2: +DI/-DI</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="text-gray-400 text-[11px] font-mono">
                                {chartData.length} Hourly Candles
                            </div>
                        </div>
                    </div>



                    {/* Pane 1: Main Candlestick Chart */}
                    <div
                        className="flex-1 min-h-[220px] w-full mt-2 relative overflow-hidden"
                        ref={chartContainerRef}
                        style={{ isolation: 'isolate' }}
                    />

                    {/* Pane 2: ADX / ADXMA Sub-Pane */}
                    <div
                        className={`h-[80px] w-full border-t ${isDarkMode ? 'border-gray-800' : 'border-slate-200'} pt-0 mt-1 relative overflow-hidden`}
                        ref={adxChartContainerRef}
                        style={{ isolation: 'isolate' }}
                    />

                    {/* Pane 3: +DI / -DI Sub-Pane */}
                    <div
                        className={`h-[80px] w-full border-t ${isDarkMode ? 'border-gray-800' : 'border-slate-200'} pt-0 mt-1 relative overflow-hidden`}
                        ref={diChartContainerRef}
                        style={{ isolation: 'isolate' }}
                    />
                </div>

                {/* Right: Diagnostics & Trade Analysis Sidebar */}
                {isSidebarOpen && (
                    <div className={`xl:col-span-1 rounded-xl p-4 flex flex-col gap-4 overflow-y-auto custom-scrollbar transition-all duration-300 border ${
                        isDarkMode ? 'bg-[#14161f] border-gray-800 text-gray-200' : 'bg-white border-slate-200 text-slate-800 shadow-lg'
                    }`}>
                        <div className={`flex items-center justify-between border-b pb-2 ${isDarkMode ? 'border-gray-800' : 'border-slate-200'}`}>
                            <h3 className={`text-sm font-bold uppercase tracking-wider flex items-center gap-2 ${isDarkMode ? 'text-gray-200' : 'text-slate-800'}`}>
                                <BarChart2 className="w-4 h-4 text-blue-500" />
                                Signal Validation Audit
                            </h3>
                            <button
                                onClick={toggleSidebar}
                                title="Collapse Audit Sidebar to expand chart"
                                className={`p-1 rounded-lg transition-colors border border-transparent ${
                                    isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800 hover:border-gray-700' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100 hover:border-slate-300'
                                }`}
                            >
                                <ChevronRight className="w-4.5 h-4.5" />
                            </button>
                        </div>


                    {/* Signal Status Box */}
                    <div className={`p-3 rounded-xl border ${
                        signalValidation?.blue_bar
                            ? 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                            : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                    }`}>
                        <div className="flex items-center gap-2 font-bold text-xs">
                            {signalValidation?.blue_bar ? <CheckCircle className="w-4 h-4 text-blue-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                            <span>{signalValidation?.blue_bar ? 'Blue Bar Active at Buy' : 'Blue Bar Inactive at Buy'}</span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">
                            {signalValidation?.blue_bar
                                ? 'The buy signal was triggered during a confirmed bullish trend bar where ADX > ADXMA and +DI led -DI.'
                                : 'At the buy execution timestamp, the blue bar condition (ADX > ADXMA & +DI > -DI) was not fully satisfied.'}
                        </p>
                    </div>

                    {/* Indicator Matrix */}
                    <div className={`p-3 rounded-xl border flex flex-col gap-2 transition-colors ${
                        isDarkMode ? 'bg-[#0f1116] border-gray-800 text-gray-300' : 'bg-slate-50 border-slate-200 text-slate-700'
                    }`}>
                        <span className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>Indicator Matrix @ Entry</span>

                        <div className={`flex justify-between items-center text-xs py-1 border-b ${isDarkMode ? 'border-gray-800/60' : 'border-slate-200'}`}>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600'}>ADX Trend Strength</span>
                            <span className="font-mono font-bold text-purple-500">
                                {typeof signalValidation?.adx === 'number' ? signalValidation.adx.toFixed(2) : '-'}
                            </span>
                        </div>

                        <div className={`flex justify-between items-center text-xs py-1 border-b ${isDarkMode ? 'border-gray-800/60' : 'border-slate-200'}`}>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600'}>ADX Moving Average (SMA 2)</span>
                            <span className="font-mono font-bold text-orange-500">
                                {typeof signalValidation?.adxma === 'number' ? signalValidation.adxma.toFixed(2) : '-'}
                            </span>
                        </div>

                        <div className={`flex justify-between items-center text-xs py-1 border-b ${isDarkMode ? 'border-gray-800/60' : 'border-slate-200'}`}>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600'}>+DI (PDI) Positive Direction</span>
                            <span className="font-mono font-bold text-emerald-500">
                                {typeof signalValidation?.pdi === 'number' ? signalValidation.pdi.toFixed(2) : '-'}
                            </span>
                        </div>

                        <div className="flex justify-between items-center text-xs py-1">
                            <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600'}>-DI (MDI) Negative Direction</span>
                            <span className="font-mono font-bold text-red-500">
                                {typeof signalValidation?.mdi === 'number' ? signalValidation.mdi.toFixed(2) : '-'}
                            </span>
                        </div>
                    </div>


                    {/* Slippage & Execution Analysis */}
                    <div className={`p-3 rounded-xl border flex flex-col gap-2 transition-colors ${
                        isDarkMode ? 'bg-[#0f1116] border-gray-800 text-gray-300' : 'bg-slate-50 border-slate-200 text-slate-700'
                    }`}>
                        <span className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>Execution Quality</span>

                        <div className={`flex justify-between items-center text-xs py-1 border-b ${isDarkMode ? 'border-gray-800/60' : 'border-slate-200'}`}>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600'}>Buy Slippage</span>
                            <span className={`font-mono font-bold ${
                                tradeRecord?.slippage_buy && Math.abs(tradeRecord.slippage_buy) <= 0.01 ? 'text-emerald-500' : 'text-amber-500'
                            }`}>
                                {tradeRecord?.slippage_buy !== null && tradeRecord?.slippage_buy !== undefined
                                    ? `${(Number(tradeRecord.slippage_buy) * 100).toFixed(2)}%`
                                    : '-'}
                            </span>
                        </div>

                        <div className="flex justify-between items-center text-xs py-1">
                            <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600'}>Sell Slippage</span>
                            <span className={`font-mono font-bold ${
                                tradeRecord?.slippage_sell && Math.abs(tradeRecord.slippage_sell) <= 0.01 ? 'text-emerald-500' : 'text-amber-500'
                            }`}>
                                {tradeRecord?.slippage_sell !== null && tradeRecord?.slippage_sell !== undefined
                                    ? `${(Number(tradeRecord.slippage_sell) * 100).toFixed(2)}%`
                                    : '-'}
                            </span>
                        </div>
                    </div>

                    {/* Footer Tip */}
                    <div className={`mt-auto pt-3 border-t flex items-center gap-2 text-[11px] ${
                        isDarkMode ? 'border-gray-800/80 text-gray-500' : 'border-slate-200 text-slate-400'
                    }`}>
                        <Info className="w-4 h-4 shrink-0 text-blue-500" />
                        <span>Hover over candlesticks to trace price vs indicator signals.</span>
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};


export default TradeSignalValidationModal;

